import { CheckCircle2Icon, Loader2Icon } from '@/components/ui/icons'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type JSX, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { subscriptionApi } from '@/features/subscription/api'
import { SUBSCRIPTION_KEY } from '@/features/subscription/hooks/useSubscription'
import { refreshAccessToken } from '@/lib/api/client'

// Ruta hermana fuera del Account Hub layout — no hereda PillTabs ni el header
// del hub. UI limpia para una página transitoria de confirmación (~2-30s).
// URL: /account/billing/processing
const searchSchema = z.object({
  session_id: z.string().optional(),
})

export const Route = createFileRoute('/_app/account_/billing/processing')({
  validateSearch: searchSchema,
  component: BillingProcessingPage,
})

function BillingProcessingPage(): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmed, setConfirmed] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const attemptsRef = useRef(0)
  const MAX_ATTEMPTS = 15 // máx 30s a 2s/intento

  useEffect(() => {
    const poll = async (): Promise<void> => {
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        // Tiempo agotado — redirigir de todas formas
        await navigate({ to: '/account/billing' })
        return
      }
      attemptsRef.current += 1

      try {
        const sub = await subscriptionApi.getSubscription()
        if (sub.status !== 'pending_checkout') {
          // Confirmado: refrescar token para actualizar subscriptionStatus en el store
          await refreshAccessToken()
          await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
          setConfirmed(true)
          pollingRef.current = setTimeout(() => {
            void navigate({ to: '/account/billing' })
          }, 2000)
          return
        }
      } catch {
        // Ignorar errores de red — seguir intentando
      }

      pollingRef.current = setTimeout(() => {
        void poll()
      }, 2000)
    }

    void poll()

    return () => {
      if (pollingRef.current !== undefined) clearTimeout(pollingRef.current)
    }
  }, [navigate, queryClient])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center">
      {confirmed ? (
        <>
          <CheckCircle2Icon className="size-16 text-success" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">¡Suscripción activada!</h1>
            <p className="mt-1 text-sm text-muted-foreground">Tu plan está listo. Redirigiendo…</p>
          </div>
        </>
      ) : (
        <>
          <Loader2Icon className="size-12 animate-spin text-primary-text" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">Confirmando pago…</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Estamos verificando tu suscripción con Stripe.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
